import { createFileRoute } from "@tanstack/react-router";
import { BreadcrumbHeader } from "@/components/primitives/breadcrumb-header";
import {
  Article,
  ArticleAside,
  ArticleBody,
  ArticleContainer,
  ArticleDescription,
  ArticleHeader,
  ArticleHeading,
  ArticleHighlightImage,
  ArticleList,
  ArticleListItem,
  ArticleParagraph,
  ArticleRelatedLink,
  ArticleSection,
  ArticleSectionHeader,
} from "@/components/primitives/article";
import { PageContainer } from "@/components/primitives/layout";
import { SingleBreadcrumb } from "@/components/primitives/single-breadcrumb";
import { UnderlineLink } from "@/components/primitives/underline-link";

const TITLE = "Which ingredients do I need to make great ice cream?";
const DESCRIPTION = "A practical guide to what should be in your pantry.";
const IMAGE_URL = "/articles/ingredients.webp";

export const Route = createFileRoute(
  "/learn/which-ingredients-do-i-need-to-make-great-ice-cream",
)({
  head: () => ({
    meta: [
      { title: `${TITLE} - Scoopulator` },
      { name: "description", content: DESCRIPTION },
    ],
  }),
  component: IceCreamIngredientsLearnPage,
});

function IceCreamIngredientsLearnPage() {
  return (
    <PageContainer>
      <ArticleContainer>
        <BreadcrumbHeader>
          <SingleBreadcrumb href="/learn" label="Learn more about ice cream" />
        </BreadcrumbHeader>

        <Article>
          <ArticleHeader>
            <ArticleHeading>{TITLE}</ArticleHeading>
            <ArticleDescription>{DESCRIPTION}</ArticleDescription>
          </ArticleHeader>
          <ArticleHighlightImage src={IMAGE_URL} alt={TITLE} />
          <ArticleBody>
            <ArticleSection>
              <ArticleParagraph>
                As discussed in{" "}
                <UnderlineLink href="/learn/why-use-an-ice-cream-calculator">
                  Why use an ice cream calculator
                </UnderlineLink>
                , the ingredients you choose have a huge impact on texture,
                sweetness, and overall quality. To make a truly great ice cream,
                you&apos;ll want some extra tools in your pantry beyond the
                basics.
              </ArticleParagraph>
              <ArticleParagraph>
                If you&apos;ve delved into the world of ice cream science
                already, the sheer variety of exotic ingredients used by
                professionals and ethusiasts can be a bit intimidating. In this
                article we&apos;ll approach it from a pratical, home cook&apos;s
                perspective: which ingredients are truly essential, which are
                highly recommended, and which are more situational?
              </ArticleParagraph>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="minimum">
                The bare minimum
              </ArticleSectionHeader>
              <ArticleParagraph>
                These are the core ingredients which form the basis of almost
                every dairy ice cream recipe. Without these, I might not call it
                ice cream at all.
              </ArticleParagraph>
              <ArticleList>
                <ArticleListItem>
                  <strong>Whole milk:</strong> The main source of water and milk
                  solids.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Heavy cream:</strong> The main source of milkfat.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Granulated sugar:</strong> The main source of
                  sweetness and freezing point depression.
                </ArticleListItem>
              </ArticleList>
              <ArticleAside>
                While whole milk and heavy cream most common (in the U.S., at
                least), you can easily substitute similar items like 2% milk or
                double cream.
              </ArticleAside>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="recommended">
                Highly recommended additions
              </ArticleSectionHeader>
              <ArticleParagraph>
                These ingredients are a little more niche, but they fill gaps
                that milk, cream, and sugar can&apos;t cover on their own.
              </ArticleParagraph>
              <ArticleList>
                <ArticleListItem>
                  <strong>Skim milk powder:</strong> A great source of lactose
                  and milk proteins without adding extra water or fat. This is a
                  a ubiquitous ingredient in premium ice creams.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Dextrose:</strong> A type of sugar which is less sweet
                  than sucrose, but has much stronger freezing point depression.
                  It helps to create recipes that are soft without being too
                  sweet.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Stabilizers:</strong> Used in tiny amounts improve the
                  texture and shelf life of ice cream. Guar gum and xanthan gum
                  are most commonly available.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Salt:</strong> You should already have this in your
                  pantry, but it deserves a mention here. Just a pinch will help
                  bring out your other flavors. It also depresses the freezing
                  point.
                </ArticleListItem>
              </ArticleList>
            </ArticleSection>

            <ArticleSection>
              <ArticleSectionHeader id="others">
                Other ingredients
              </ArticleSectionHeader>
              <ArticleParagraph>
                These aren&apos;t required to make great ice cream, but can be
                useful in certain situations.
              </ArticleParagraph>
              <ArticleList>
                <ArticleListItem>
                  <strong>Emulsifiers:</strong> Helps with whipping air into the
                  ice cream. Traditionally egg yolks are used for this purpose,
                  but soy or sunflower lecithin are good alternatives if you
                  want a cleaner flavor.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Inulin:</strong> A vegetable fiber which can be used
                  to add body and creaminess in low-fat ice creams or sorbets.
                </ArticleListItem>
                <ArticleListItem>
                  <strong>Allulose:</strong> A popular alternative sweetener
                  with identical properties to dextrose, but only a tenth of the
                  calories.
                </ArticleListItem>
              </ArticleList>
              <ArticleParagraph>
                This list is by no means exhaustive. The world of ice cream
                making is full of interesting ingredients to experiment with,
                but I find that the ones listed above cover 95% of what most
                home cooks will need.
              </ArticleParagraph>
              <ArticleRelatedLink
                className="mt-8"
                href="/calc"
                title="Use Scoopulator's free ice cream calculator"
                label="Try it out"
                srLabel="Ice cream calculator"
                description="Build a recipe, hit your targets, and iterate with confidence."
              />
            </ArticleSection>
          </ArticleBody>
        </Article>
      </ArticleContainer>
    </PageContainer>
  );
}
